import { api } from "@/lib/orgApi";

// ---- systems & extension URLs ----------------------------------------------
export const V2_0203 = "http://terminology.hl7.org/CodeSystem/v2-0203";
export const SSN_SYSTEM = "http://hl7.org/fhir/sid/us-ssn";
export const BCP47 = "urn:ietf:bcp:47";
export const MARITAL_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-MaritalStatus";
export const CONTACT_REL_SYSTEM = "http://terminology.hl7.org/CodeSystem/v2-0131";
export const RACE_SYSTEM = "urn:oid:2.16.840.1.113883.6.238";
export const SNOMED = "http://snomed.info/sct";
export const LOINC = "http://loinc.org";

export const RACE_URL = "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race";
export const ETHNICITY_URL = "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity";
export const BIRTHSEX_URL = "http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex";
export const GENDER_IDENTITY_URL = "http://hl7.org/fhir/us/core/StructureDefinition/us-core-genderIdentity";
export const PRONOUNS_URL = "http://hl7.org/fhir/StructureDefinition/individual-pronouns";

export const MRN_TYPE_CODE = "MR";

// ---- value sets ------------------------------------------------------------
export const GENDERS = [
  { code: "male", display: "Male" },
  { code: "female", display: "Female" },
  { code: "other", display: "Other" },
  { code: "unknown", display: "Unknown" },
];

export const BIRTHSEX = [
  { code: "F", display: "Female" },
  { code: "M", display: "Male" },
  { code: "UNK", display: "Unknown" },
];

export const RACE_CATEGORIES = [
  { code: "1002-5", display: "American Indian or Alaska Native" },
  { code: "2028-9", display: "Asian" },
  { code: "2054-5", display: "Black or African American" },
  { code: "2076-8", display: "Native Hawaiian or Other Pacific Islander" },
  { code: "2106-3", display: "White" },
];

export const ETHNICITY_CATEGORIES = [
  { code: "2135-2", display: "Hispanic or Latino" },
  { code: "2186-5", display: "Not Hispanic or Latino" },
];

export const GENDER_IDENTITIES = [
  { code: "446151000124109", display: "Identifies as male gender" },
  { code: "446141000124107", display: "Identifies as female gender" },
  { code: "446131000124102", display: "Identifies as non-conforming gender" },
  { code: "407377005", display: "Female-to-male transsexual" },
  { code: "407376001", display: "Male-to-female transsexual" },
  { code: "ASKU", display: "Asked but unknown" },
];

export const PRONOUNS = [
  { code: "LA29518-0", display: "he/him/his/his/himself" },
  { code: "LA29519-8", display: "she/her/her/hers/herself" },
  { code: "LA29520-6", display: "they/them/their/theirs/themselves" },
];

export const MARITAL_STATUSES = [
  { code: "S", display: "Never Married" },
  { code: "M", display: "Married" },
  { code: "D", display: "Divorced" },
  { code: "W", display: "Widowed" },
  { code: "L", display: "Legally Separated" },
  { code: "U", display: "Unmarried" },
];

export const LANGUAGES = [
  { code: "en", display: "English" },
  { code: "es", display: "Spanish" },
  { code: "zh", display: "Chinese" },
  { code: "fr", display: "French" },
  { code: "ru", display: "Russian" },
  { code: "ar", display: "Arabic" },
];

export const RELATIONSHIP_TYPES = [
  { code: "C", display: "Emergency Contact" },
  { code: "N", display: "Next-of-Kin" },
  { code: "O", display: "Other" },
];

const disp = (list, code) => list.find((x) => x.code === code)?.display;

export function fetchOrganizations() {
  return api.get("/organizations").then((r) => r.data.items || []);
}

export function mrnOf(r) {
  return (r?.identifier || []).find((i) =>
    (i.type?.coding || []).some((c) => c.code === MRN_TYPE_CODE)
  )?.value || "—";
}

// ---- form -> FHIR payload (MRN is NOT included; server generates it) -------
export function formToPayload(f, orgs) {
  const org = (orgs || []).find((o) => o.id === f.managingOrgId);

  const identifier = [];
  if (f.ssn?.trim())
    identifier.push({
      use: "official",
      type: { coding: [{ system: V2_0203, code: "SS", display: "Social Security number" }] },
      system: SSN_SYSTEM,
      value: f.ssn.trim(),
    });

  const given = [f.given, f.middle].map((s) => s?.trim()).filter(Boolean);
  const name = [{
    use: "official",
    family: f.family?.trim(),
    ...(given.length ? { given } : {}),
    ...(f.suffix?.trim() ? { suffix: [f.suffix.trim()] } : {}),
  }];

  const telecom = [];
  if (f.mobile?.trim()) telecom.push({ system: "phone", value: f.mobile.trim(), use: "mobile" });
  if (f.homePhone?.trim()) telecom.push({ system: "phone", value: f.homePhone.trim(), use: "home" });
  if (f.email?.trim()) telecom.push({ system: "email", value: f.email.trim(), use: "home" });

  const line = [f.line1, f.line2].map((s) => s?.trim()).filter(Boolean);
  const hasAddr = line.length || f.city || f.state || f.postalCode;
  const address = hasAddr
    ? [{
        use: "home", type: "both",
        ...(line.length ? { line } : {}),
        ...(f.city?.trim() ? { city: f.city.trim() } : {}),
        ...(f.state?.trim() ? { state: f.state.trim() } : {}),
        ...(f.postalCode?.trim() ? { postalCode: f.postalCode.trim() } : {}),
        ...(f.country?.trim() ? { country: f.country.trim() } : {}),
      }]
    : undefined;

  const maritalStatus = f.marital
    ? { coding: [{ system: MARITAL_SYSTEM, code: f.marital, display: disp(MARITAL_STATUSES, f.marital) }] }
    : undefined;

  const communication = f.language
    ? [{ language: { coding: [{ system: BCP47, code: f.language, display: disp(LANGUAGES, f.language) }] }, preferred: true }]
    : undefined;

  const contact = (f.contactFamily?.trim() || f.contactGiven?.trim() || f.contactPhone?.trim())
    ? [{
        relationship: [{ coding: [{ system: CONTACT_REL_SYSTEM, code: f.contactRel || "C", display: disp(RELATIONSHIP_TYPES, f.contactRel || "C") }] }],
        name: {
          ...(f.contactFamily?.trim() ? { family: f.contactFamily.trim() } : {}),
          ...(f.contactGiven?.trim() ? { given: [f.contactGiven.trim()] } : {}),
        },
        ...(f.contactPhone?.trim() ? { telecom: [{ system: "phone", value: f.contactPhone.trim(), use: "mobile" }] } : {}),
      }]
    : undefined;

  const extension = [];
  if (f.race) {
    const d = disp(RACE_CATEGORIES, f.race);
    extension.push({ url: RACE_URL, extension: [
      { url: "ombCategory", valueCoding: { system: RACE_SYSTEM, code: f.race, display: d } },
      { url: "text", valueString: d },
    ]});
  }
  if (f.ethnicity) {
    const d = disp(ETHNICITY_CATEGORIES, f.ethnicity);
    extension.push({ url: ETHNICITY_URL, extension: [
      { url: "ombCategory", valueCoding: { system: RACE_SYSTEM, code: f.ethnicity, display: d } },
      { url: "text", valueString: d },
    ]});
  }
  if (f.birthsex) extension.push({ url: BIRTHSEX_URL, valueCode: f.birthsex });
  if (f.genderIdentity) {
    const d = disp(GENDER_IDENTITIES, f.genderIdentity);
    extension.push({ url: GENDER_IDENTITY_URL, valueCodeableConcept: { coding: [{ system: SNOMED, code: f.genderIdentity, display: d }], text: d } });
  }
  if (f.pronouns) {
    const d = disp(PRONOUNS, f.pronouns);
    extension.push({ url: PRONOUNS_URL, extension: [
      { url: "value", valueCodeableConcept: { coding: [{ system: LOINC, code: f.pronouns, display: d }], text: d } },
    ]});
  }

  const payload = {
    active: true,
    ...(identifier.length ? { identifier } : {}),
    name,
    gender: f.gender,
    ...(f.birthDate ? { birthDate: f.birthDate } : {}),
    ...(telecom.length ? { telecom } : {}),
    ...(address ? { address } : {}),
    ...(maritalStatus ? { maritalStatus } : {}),
    ...(communication ? { communication } : {}),
    ...(contact ? { contact } : {}),
    managingOrganization: { reference: `Organization/${f.managingOrgId}`, display: org?.name || undefined },
    ...(extension.length ? { extension } : {}),
  };

  if (f.deceased) {
    if (f.deceasedDate) payload.deceasedDateTime = f.deceasedDate;
    else payload.deceasedBoolean = true;
  }
  return payload;
}

// ---- FHIR resource -> flat form (for edit) ---------------------------------
export function resourceToForm(r) {
  const ssn = (r.identifier || []).find((i) => i.system === SSN_SYSTEM)?.value || "";
  const nm = (r.name && r.name[0]) || {};
  const tele = (sys, use) => (r.telecom || []).find((t) => t.system === sys && (!use || t.use === use))?.value || "";
  const addr = (r.address && r.address[0]) || {};
  const ext = (url) => (r.extension || []).find((e) => e.url === url);
  const sub = (e, url) => (e?.extension || []).find((x) => x.url === url);
  const race = ext(RACE_URL);
  const eth = ext(ETHNICITY_URL);
  const bs = ext(BIRTHSEX_URL);
  const gi = ext(GENDER_IDENTITY_URL);
  const pr = ext(PRONOUNS_URL);
  const contact = (r.contact && r.contact[0]) || {};
  return {
    ssn,
    family: nm.family || "",
    given: nm.given?.[0] || "",
    middle: nm.given?.[1] || "",
    suffix: nm.suffix?.[0] || "",
    gender: r.gender || "",
    birthDate: r.birthDate || "",
    mobile: tele("phone", "mobile"),
    homePhone: tele("phone", "home"),
    email: tele("email"),
    line1: addr.line?.[0] || "",
    line2: addr.line?.[1] || "",
    city: addr.city || "",
    state: addr.state || "",
    postalCode: addr.postalCode || "",
    country: addr.country || "US",
    marital: r.maritalStatus?.coding?.[0]?.code || "",
    language: r.communication?.[0]?.language?.coding?.[0]?.code || "",
    race: sub(race, "ombCategory")?.valueCoding?.code || "",
    ethnicity: sub(eth, "ombCategory")?.valueCoding?.code || "",
    birthsex: bs?.valueCode || "",
    genderIdentity: gi?.valueCodeableConcept?.coding?.[0]?.code || "",
    pronouns: sub(pr, "value")?.valueCodeableConcept?.coding?.[0]?.code || "",
    managingOrgId: (r.managingOrganization?.reference || "").split("/")[1] || "",
    contactRel: contact.relationship?.[0]?.coding?.[0]?.code || "C",
    contactFamily: contact.name?.family || "",
    contactGiven: contact.name?.given?.[0] || "",
    contactPhone: contact.telecom?.[0]?.value || "",
    deceased: r.deceasedBoolean === true || !!r.deceasedDateTime,
    deceasedDate: r.deceasedDateTime || "",
  };
}
